import { decodeFilePath, getPathInfo } from '@sveltia/utils/file';
import { escapeRegExp, stripSlashes } from '@sveltia/utils/string';
import { flatten } from 'flat';

import {
  allAssetFolders,
  getAssetFolder,
  globalAssetFolder,
  selectedAssetFolder,
} from '$lib/services/assets/folders';
import { allAssets, focusedAsset } from '$lib/services/assets/state';
import {
  focusedSubfolder,
  getDirName,
  selectedSubfolderPath,
} from '$lib/services/assets/subfolders';
import { fillTemplate } from '$lib/services/common/template';
import {
  ESCAPED_PLACEHOLDER_REGEX,
  TEMPLATE_TAG_REGEX,
} from '$lib/services/common/template/constants';
import { hasTemplateTags } from '$lib/services/common/template/tags';
import { getCollection } from '$lib/services/contents/collection';
import { isCollectionIndexFile } from '$lib/services/contents/collection/entries/index-file';
import { getCollectionFilesByEntry } from '$lib/services/contents/collection/files';
import { getAssociatedCollections } from '$lib/services/contents/entry/collections';
import { getOrCreate, memoizeOnSource } from '$lib/services/utils/cache';
import { createPath, resolvePath, stripPathPrefix } from '$lib/services/utils/file';
import { createRootEffect } from '$lib/services/utils/state.svelte';

/**
 * @import {
 * Asset,
 * AssetFolderInfo,
 * Entry,
 * InternalCollection,
 * InternalCollectionFile,
 * TypedFieldKeyPath,
 * } from '$lib/types/private';
 */

/**
 * Get a Map from asset path to Asset, used for O(1) path lookups. Rebuilt only when `allAssets` is
 * replaced.
 * @type {() => Map<string, Asset>}
 */
const getAssetPathMap = memoizeOnSource(
  () => allAssets.current,
  (_allAssets) => new Map(_allAssets.map((asset) => [asset.path, asset])),
);

/**
 * Get an asset by its internal repository path.
 * @param {string} path Asset path.
 * @returns {Asset | undefined} Asset.
 */
export const getAssetByInternalPath = (path) => getAssetPathMap().get(path);

/**
 * Get a stable, unique key for the given asset, to be used as its identity in a list. An unsaved
 * asset is keyed by its blob URL, because it can temporarily share a path with the saved asset it’s
 * going to overwrite, and two pending files can share one as well. A saved asset is keyed by its
 * path, which is unique within the repository, and unlike its blob URL — only generated when the
 * file is fetched — never changes while the asset is listed.
 * @param {Asset} asset Asset.
 * @returns {string} Key.
 */
export const getAssetKey = ({ unsaved, blobURL, path }) => (unsaved && blobURL ? blobURL : path);

/**
 * Resolve template tags in an asset folder’s `internalPath`, such as `/assets/images/{{slug}}`,
 * using the given entry’s content.
 * @param {object} args Arguments.
 * @param {string} args.internalPath Internal path that may contain template tags.
 * @param {string} [args.collectionName] Collection name associated with the asset folder. It’s
 * `undefined` for the global asset folder as well as field-level asset folders in custom editor
 * components.
 * @param {Entry} [args.entry] Associated entry. Can be `undefined` when editing a new draft.
 * @returns {string | undefined} Resolved path, which is the given `internalPath` itself if it has
 * no template tags, or `undefined` if the tags cannot be resolved, typically because the entry has
 * not been saved yet.
 */
export const fillInternalPathTemplate = ({ internalPath, collectionName, entry }) => {
  if (!TEMPLATE_TAG_REGEX.test(internalPath)) {
    return internalPath;
  }

  const collection = collectionName
    ? getCollection(collectionName)
    : entry
      ? getAssociatedCollections(entry)[0]
      : undefined;

  if (!entry || !collection) {
    // Cannot resolve the path
    return undefined;
  }

  const { slug, locales } = entry;
  const { defaultLocale } = collection._i18n;
  const locale = defaultLocale in locales ? defaultLocale : Object.keys(locales)[0];
  const { content, path: entryFilePath } = locales[locale];

  return fillTemplate(internalPath, {
    type: 'media_folder',
    collection,
    content: flatten(content),
    currentSlug: slug,
    entryFilePath,
    isIndexFile: isCollectionIndexFile(collection, entry),
  });
};

/**
 * Find an asset by a relative path, using the associated entry and collection to help locate it.
 * @param {object} context Context.
 * @param {string} context.path Saved relative path.
 * @param {Entry} context.entry Associated entry to be used to help locate an asset from a relative
 * path. Can be `undefined` when editing a new draft.
 * @param {InternalCollection} context.collection Associated collection.
 * @param {InternalCollectionFile} [context.file] Associated collection file.
 * @param {string} [context.componentName] Custom editor component name for a field-level asset
 * folder.
 * @param {TypedFieldKeyPath} [context.typedKeyPath] Field key path for field-level media folders.
 * @returns {Asset | undefined} Found asset.
 */
export const getAssetByRelativePathAndCollection = ({
  path,
  entry,
  collection,
  file,
  componentName,
  typedKeyPath,
}) => {
  const { locales } = entry;

  const {
    _i18n: { defaultLocale },
  } = file ?? collection;

  // When a field-level key path is provided, look up the field-specific asset folder first so we
  // use the correct `media_folder` (e.g. a field with `media_folder: images1` instead of the
  // collection-level `/src/assets/images/blog`).
  const fieldFolder = typedKeyPath
    ? getAssetFolder({
        collectionName: collection.name,
        fileName: file?.name,
        componentName,
        typedKeyPath,
      })
    : undefined;

  let mediaFolder = fieldFolder?.entryRelative
    ? (fieldFolder.internalSubPath ?? '')
    : /** @type {string | undefined} */ ((file ?? collection).media_folder);

  const publicFolder = fieldFolder?.publicPath
    ? fieldFolder.publicPath
    : /** @type {string | undefined} */ ((file ?? collection).public_folder);

  const locale = defaultLocale in locales ? defaultLocale : Object.keys(locales)[0];
  const { path: entryFilePath, content: entryContent } = locales[locale];

  if (!entryFilePath || !entryContent) {
    return undefined;
  }

  // Resolve template tags like `{{filename}}` or `{{slug}}` in an entry-relative `media_folder`
  // (e.g. Hexo’s Post Asset Folder convention) so the asset can be located correctly.
  // @see https://github.com/sveltia/sveltia-cms/issues/853
  if (mediaFolder && hasTemplateTags(mediaFolder)) {
    mediaFolder = fillTemplate(mediaFolder, {
      type: 'media_folder',
      collection,
      content: entryContent,
      currentSlug: entry.slug,
      entryFilePath,
      isIndexFile: isCollectionIndexFile(collection, entry),
    });
  }

  // Directory of the entry file, which is an empty string for an entry file at the repository root
  const entryFolder = getDirName(entryFilePath);
  // Strip the `media_folder` prefix from the stored path before joining with `mediaFolder`, to
  // avoid duplication when the stored value already includes the media folder (e.g.
  // `images/photo.jpg`). Also normalize `./` prefix since `./images/photo.jpg` and
  // `images/photo.jpg` are equivalent relative paths.
  // `media_folder` is normalized the same way, since `./images` and `images/` are equivalent to
  // `images`.
  const normalizedPath = path.replace(/^\.\//, '');
  const normalizedMediaFolder = mediaFolder?.replace(/^\.\//, '').replace(/\/$/, '');
  let localPath = stripPathPrefix(normalizedPath, normalizedMediaFolder);
  let resolvedPath;

  // When `media_folder` is absolute (starts with `/`) and `public_folder` is entry-relative (e.g.
  // starts with `.` or `..`), we need special handling for the Astro content collections pattern
  // where images are stored in a shared folder but referenced with relative paths from each entry.
  // Strip the `public_folder` prefix from the stored value and resolve directly against the
  // absolute `media_folder`, bypassing `entryFolder` concatenation.
  if (mediaFolder?.startsWith('/') && publicFolder) {
    // Normalize `public_folder` by removing leading `./`
    const normalizedPublicFolder = publicFolder.replace(/^\.\//, '');

    // Check if the stored path starts with the public folder
    if (normalizedPath.startsWith(`${normalizedPublicFolder}/`)) {
      // Strip the public folder prefix to get just the filename/subpath
      localPath = normalizedPath.slice(normalizedPublicFolder.length + 1);
    }

    // Resolve against the absolute media_folder (strip leading `/` to make it repo-relative)
    resolvedPath = resolvePath(createPath([mediaFolder.slice(1), localPath]));
  } else {
    // Original logic: concatenate entryFolder + mediaFolder + localPath for entry-relative folders
    resolvedPath = resolvePath(createPath([entryFolder, mediaFolder, localPath]));
  }

  return getAssetPathMap().get(resolvedPath);
};

/**
 * Get an asset by a relative public path typically stored as an image field value.
 * @param {object} args Arguments.
 * @param {string} args.path Saved relative path.
 * @param {Entry} [args.entry] Associated entry to be used to help locate an asset from a relative
 * path. Can be `undefined` when editing a new draft.
 * @param {string} [args.collectionName] Collection name, used when no entry is available.
 * @param {string} [args.fileName] Collection file name. File/singleton collection only.
 * @param {string} [args.componentName] Custom editor component name for a field-level asset folder.
 * @param {TypedFieldKeyPath} [args.typedKeyPath] Field key path for field-level media folders.
 * @returns {Asset | undefined} Corresponding asset.
 */
export const getAssetByRelativePath = ({
  path,
  entry,
  collectionName,
  fileName,
  componentName,
  typedKeyPath,
}) => {
  if (!entry) {
    // Without an entry we use collectionName/fileName to scan configured folders. For
    // entry-relative folders, internalPath + internalSubPath is used as a best-effort path.
    const scanningFolders = /** @type {AssetFolderInfo[]} */ (
      [
        componentName ? getAssetFolder({ componentName, typedKeyPath }) : undefined,
        collectionName && typedKeyPath
          ? getAssetFolder({ collectionName, fileName, typedKeyPath })
          : undefined,
        collectionName ? getAssetFolder({ collectionName, fileName }) : undefined,
        collectionName ? getAssetFolder({ collectionName }) : undefined,
        globalAssetFolder.current,
      ].filter((folder) => !!folder && !folder.hasTemplateTags)
    );

    const foundAsset = scanningFolders
      .values()
      .map((folder) => {
        // Strip the publicPath prefix from the stored path to get the bare filename/subpath, so
        // that e.g. `uploads/photo.jpg` with publicPath `/uploads` resolves to `uploads/photo.jpg`
        // internally rather than `uploads/uploads/photo.jpg`.
        const localPath = stripPathPrefix(path, folder.publicPath?.replace(/^\//, ''));

        return getAssetPathMap().get(
          createPath([folder.internalPath, folder.internalSubPath ?? '', localPath]),
        );
      })
      .find(Boolean);

    return foundAsset ?? getAssetPathMap().get(path);
  }

  // Stop at the first collection or collection file that has the asset
  const foundAsset = getAssociatedCollections(entry)
    .values()
    .flatMap((collection) => {
      const collectionFiles = getCollectionFilesByEntry(collection, entry);
      const args = { path, entry, collection, componentName, typedKeyPath };
      /** @type {(InternalCollectionFile | undefined)[]} */
      const files = collectionFiles.length ? collectionFiles : [undefined];

      return files.values().map((file) => getAssetByRelativePathAndCollection({ ...args, file }));
    })
    .find(Boolean);

  return (
    foundAsset ??
    // Fall back to exact match at the root folder
    getAssetPathMap().get(path)
  );
};

/**
 * Cache of {@link getPublicPathRegex} results, keyed by the asset folder.
 * @type {WeakMap<AssetFolderInfo, RegExp>}
 */
const publicPathRegexCache = new WeakMap();

/**
 * Get a regular expression matching a directory at or below the given folder’s public path, with
 * any template tag in the path matching a segment.
 * @param {AssetFolderInfo} folder Asset folder.
 * @returns {RegExp} Regular expression.
 */
const getPublicPathRegex = (folder) =>
  getOrCreate(publicPathRegexCache, folder, () => {
    const publicPath = folder.publicPath ?? '';
    const normalizedPath = escapeRegExp(publicPath).replace(ESCAPED_PLACEHOLDER_REGEX, '.+?');

    return new RegExp(`^${normalizedPath}${publicPath ? '(?=\\/|$)' : '$'}`);
  });

/**
 * Get an asset by an absolute public path typically stored as an image field value.
 * @param {object} args Arguments.
 * @param {string} args.path Saved absolute path.
 * @param {Entry} [args.entry] Associated entry to be used to help locate an asset from a relative
 * path. Can be `undefined` when editing a new draft.
 * @param {string} args.collectionName Collection name.
 * @param {string} [args.fileName] Collection file name. File/singleton collection only.
 * @param {string} [args.componentName] Custom editor component name for a field-level asset folder.
 * @param {TypedFieldKeyPath} [args.typedKeyPath] Field key path for field-level media folders.
 * @returns {Asset | undefined} Corresponding asset.
 */
export const getAssetByAbsolutePath = ({
  path,
  entry,
  collectionName,
  fileName,
  componentName,
  typedKeyPath,
}) => {
  const exactMatch = getAssetPathMap().get(stripSlashes(path));

  if (exactMatch) {
    return exactMatch;
  }

  const { dirname: dirName = '', basename: baseName } = getPathInfo(path);

  /**
   * Look for the asset in the given folder.
   * @param {AssetFolderInfo} folder Asset folder.
   * @returns {Asset | undefined} Asset, if found.
   */
  const findInFolder = (folder) => {
    const { publicPath, collectionName: _collectionName } = folder;
    let { internalPath } = folder;

    if (internalPath !== undefined) {
      // Deal with template tags like `/assets/images/{{slug}}`
      internalPath = fillInternalPathTemplate({
        internalPath,
        collectionName: _collectionName,
        entry,
      });

      if (internalPath === undefined) {
        // Cannot resolve the path
        return undefined;
      }
    }

    // Handle assets stored in a subfolder of the internal path
    if (publicPath && internalPath && dirName) {
      if (publicPath === '/') {
        internalPath = `${internalPath}${dirName}`;
      } else if (dirName.startsWith(`${publicPath}/`)) {
        internalPath = dirName.replace(publicPath, internalPath);
      }
    }

    return getAssetPathMap().get(createPath([internalPath, baseName]));
  };

  const scanningFolders = /** @type {AssetFolderInfo[]} */ (
    [
      componentName ? getAssetFolder({ componentName, typedKeyPath }) : undefined,
      typedKeyPath ? getAssetFolder({ collectionName, fileName, typedKeyPath }) : undefined,
      getAssetFolder({ collectionName, fileName }),
      getAssetFolder({ collectionName }),
      globalAssetFolder.current,
    ].filter((folder) => !!folder)
  );

  /** @type {Asset | undefined} */
  let foundAsset = undefined;

  // Use `some` to stop scanning folders as soon as the asset is found
  scanningFolders.some((folder) => {
    foundAsset = findInFolder(folder);

    return !!foundAsset;
  });

  if (foundAsset) {
    return foundAsset;
  }

  // Fall back to the last folder whose public path matches the directory. This is only worked out
  // once the likely folders above have missed, as it tests the path against every asset folder
  const matchingFolder = allAssetFolders.current.findLast((folder) =>
    getPublicPathRegex(folder).test(dirName),
  );

  return matchingFolder ? findInFolder(matchingFolder) : undefined;
};

/**
 * Check if a path is a relative path. A path starting with `@`, like `@assets/images/...` is a
 * special case, considered as an absolute path.
 * @param {string} path Path to check.
 * @returns {boolean} `true` if the path is relative.
 */
export const isRelativePath = (path) => !/^[/@]/.test(path);

/**
 * Get an asset by a public path typically stored as an image field value.
 * @param {object} args Arguments.
 * @param {string} args.value Saved absolute path or relative path.
 * @param {Entry} [args.entry] Associated entry to be used to help locate an asset from a relative
 * path. Can be `undefined` when editing a new draft.
 * @param {string} args.collectionName Collection name.
 * @param {string} [args.fileName] Collection file name. File/singleton collection only.
 * @param {string} [args.componentName] Custom editor component name for a field-level asset folder.
 * @param {TypedFieldKeyPath} [args.typedKeyPath] Field key path for field-level media folders.
 * @returns {Asset | undefined} Corresponding asset.
 */
export const getAssetByPath = ({ value, ...rest }) => {
  // Remove potential fragment before decoding
  const path = decodeFilePath(value.split('#')[0]);

  // Handle a relative path
  if (isRelativePath(path)) {
    return getAssetByRelativePath({ path, ...rest });
  }

  return getAssetByAbsolutePath({ path, ...rest });
};

/**
 * Check if an asset belongs to the given asset folder.
 * @param {Asset} asset Asset.
 * @param {AssetFolderInfo} folder Folder info.
 * @returns {boolean} Result.
 */
export const isAssetInFolder = ({ folder: assetFolder }, folder) =>
  assetFolder === folder ||
  (assetFolder.collectionName === folder.collectionName &&
    assetFolder.fileName === folder.fileName &&
    assetFolder.typedKeyPath === folder.typedKeyPath &&
    assetFolder.isIndexFile === folder.isIndexFile &&
    assetFolder.internalPath === folder.internalPath &&
    assetFolder.internalSubPath === folder.internalSubPath &&
    assetFolder.publicPath === folder.publicPath &&
    assetFolder.entryRelative === folder.entryRelative &&
    assetFolder.hasTemplateTags === folder.hasTemplateTags);

/**
 * Get a list of assets stored in the given collection defined folder.
 * @param {AssetFolderInfo} folder Folder info.
 * @returns {Asset[]} Assets.
 */
export const getAssetsByFolder = (folder) =>
  allAssets.current.filter((asset) => isAssetInFolder(asset, folder));

/**
 * Get a list of assets stored in the given internal directory.
 * @param {string} dirname Directory path. An empty string for the repository root.
 * @returns {Asset[]} Assets.
 */
export const getAssetsByDirName = (dirname) =>
  // `getPathInfo()` has no directory for a file at the root
  allAssets.current.filter((a) => (getPathInfo(a.path).dirname ?? '') === dirname);

// Reset the asset selection when a different folder or subfolder is selected
createRootEffect(() => {
  // Read the folder and subfolder so that the effect re-runs whenever either changes
  void selectedAssetFolder.current;
  void selectedSubfolderPath.current;
  focusedAsset.current = undefined;
  focusedSubfolder.current = undefined;
});

/**
 * Get a list of duplicate files based on their names compared to existing assets.
 * @param {File[]} files The list of files to check for duplicates.
 * @param {Asset[]} assets The list of existing assets to compare against.
 * @returns {File[]} An array of files that have duplicate names in the existing assets.
 */
export const getDuplicateFiles = (files, assets) => {
  if (!assets.length || !files.length) {
    return [];
  }

  const existingNames = new Set(assets.map(({ name }) => name.normalize().toLowerCase()));

  return files.filter((file) => existingNames.has(file.name.normalize().toLowerCase()));
};
